// Converts scroll position of a scrollable page/stage into normalized progress + vh motion values.

import { useEffect, useRef, type RefObject } from 'react';
import { useMotionValue, type MotionValue } from 'framer-motion';
import { clamp } from '../utilities/scrollTimelineMath';

const STAGE_VIEWPORT_VH = 100;

export type SystemScrollController = {
  pageRef: RefObject<HTMLElement>;
  stageRef: RefObject<HTMLElement>;
  scrollProgress: MotionValue<number>;
  scrollVh: MotionValue<number>;
  stageHeightVh: number;
};

export const useSystemScrollVh = (
  timelineVh: number
): SystemScrollController => {
  const pageRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLElement>(null);
  const scrollProgress = useMotionValue(0);
  const scrollVh = useMotionValue(0);
  const stageHeightVh = timelineVh + STAGE_VIEWPORT_VH;

  useEffect(() => {
    const pageElement = pageRef.current;
    const stageElement = stageRef.current;
    if (!pageElement || !stageElement) return;

    let frameId = 0;

    const updateScrollPosition = () => {
      frameId = 0;
      const travelDistance = Math.max(
        stageElement.offsetHeight - pageElement.clientHeight,
        1
      );
      const scrollStart = stageElement.offsetTop;
      const nextProgress = clamp(
        (pageElement.scrollTop - scrollStart) / travelDistance
      );

      scrollProgress.set(nextProgress);
      scrollVh.set(nextProgress * timelineVh);
    };

    const requestUpdate = () => {
      if (frameId !== 0) return;
      frameId = window.requestAnimationFrame(updateScrollPosition);
    };

    requestUpdate();
    pageElement.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    return () => {
      if (frameId !== 0) window.cancelAnimationFrame(frameId);
      pageElement.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
  }, [scrollProgress, scrollVh, timelineVh]);

  useEffect(() => {
    const pageElement = pageRef.current;
    if (!pageElement) return;

    let touchStartY = 0;
    const getMaxScrollTop = (): number =>
      Math.max(pageElement.scrollHeight - pageElement.clientHeight, 0);

    const handleTouchStart = (event: TouchEvent) => {
      touchStartY = event.touches[0]?.clientY ?? 0;
    };

    const handleTouchMove = (event: TouchEvent) => {
      const currentY = event.touches[0]?.clientY ?? touchStartY;
      const deltaY = touchStartY - currentY;
      const atTop = pageElement.scrollTop <= 0;
      const atBottom = pageElement.scrollTop >= getMaxScrollTop() - 1;
      if ((atTop && deltaY < 0) || (atBottom && deltaY > 0)) {
        event.preventDefault();
      }
    };

    pageElement.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });
    pageElement.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });

    return () => {
      pageElement.removeEventListener('touchstart', handleTouchStart);
      pageElement.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  return {
    pageRef: pageRef as RefObject<HTMLElement>,
    stageRef: stageRef as RefObject<HTMLElement>,
    scrollProgress,
    scrollVh,
    stageHeightVh,
  };
};
